import { useTranslation } from "react-i18next";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { isEmpty } from "lodash-es";
import { ReplayOnboardingButton } from "~/routes/(restricted)/byggeradar/-components/onboarding/ByggeRadarOnboarding";
import { TOUR_ANCHORS } from "~/routes/(restricted)/byggeradar/-components/onboarding/tour-steps";
import {
  type ProposalSearch,
  proposalSearchSchema,
} from "~/routes/(restricted)/byggeradar/-schemas/proposal-search";
import { AgentsCard } from "~/routes/(restricted)/byggeradar/_access/(landing)/-partials/AgentsCard";
import { MapCard } from "~/routes/(restricted)/byggeradar/_access/(landing)/-partials/MapCard";
import { ProposalCollectionsCard } from "~/routes/(restricted)/byggeradar/_access/(landing)/-partials/ProposalCollectionsCard";
import { SearchBar } from "~/routes/(restricted)/byggeradar/_access/(landing)/-partials/SearchBar";
import { cleanToSchema } from "~/utils/schema";

// NOTE: LandingPageV1 specifics, remove when obsolete
let _savedSearch: ProposalSearch = {};

export const Route = createFileRoute(
  "/(restricted)/byggeradar/_access/(landing)",
)({
  component: LandingPage,
  validateSearch: proposalSearchSchema,
  // NOTE: LandingPageV1 specifics, remove when obsolete
  beforeLoad: ({ search, location }) => {
    // if the search is marked to restore, we load the saved search
    if (
      location.pathname.startsWith("/byggeradar") &&
      !location.state.suppressLifeCycleRedirects &&
      isEmpty(search) &&
      !isEmpty(_savedSearch)
    ) {
      throw redirect({
        to: "/byggeradar",
        search: _savedSearch,
        replace: true,
      });
    }
  },
  loader: async ({ context: { i18n } }) => {
    await i18n.loadNamespaces("proposals");

    const title = i18n.t("proposals:landing.meta.title");

    return { meta: { title } };
  },

  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
  // NOTE: LandingPageV1 specifics, remove when obsolete
  onLeave: (match) => {
    // leaving the route, we save the current search (proposalSearchSchema) in memory, for later explicit restore.
    _savedSearch = cleanToSchema(match.search, proposalSearchSchema);
  },
});

function LandingPage() {
  const { t } = useTranslation(["proposals"]);

  return (
    <div className="mx-6 flex w-full max-w-[1384px] flex-col items-center gap-12 lg:h-full lg:[@media(max-height:56rem)]:gap-8">
      <header className="mt-22 flex flex-col gap-6 lg:[@media(max-height:56rem)]:mt-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-center text-4xl font-bold">
            {t("proposals:landing.search.title")}
          </h1>
          <p className="text-muted text-center text-lg">
            {t("proposals:landing.search.description")}
          </p>
        </div>
        <div className="flex justify-center">
          <ReplayOnboardingButton />
        </div>
      </header>

      <div
        data-tour={TOUR_ANCHORS.search}
        className="flex w-2/3 justify-center"
      >
        <SearchBar />
      </div>

      <div
        data-tour={TOUR_ANCHORS.landingCards}
        className="grid grid-cols-1 items-stretch gap-5 pb-8 lg:min-h-[22rem] lg:w-full lg:flex-1 lg:grid-cols-3"
      >
        <AgentsCard />
        <ProposalCollectionsCard />
        <MapCard />
      </div>
    </div>
  );
}
